import { TOKEN_ADDRESS_BY_CHAIN } from '@ui/services/contract/constants';
import { Address } from '@ui/types/alias';
import { ChainId, ChainName } from '@ui/types/chain';
import {
  PayablePoolToken,
  PayablePoolTokenWithoutNative,
  PoolToken,
  UnstablePoolToken,
} from '@ui/types/token';

import { getChainName } from '../chain';
import { notListedDate } from '../listedAt';
import { PoolTokenSymbol } from './tokenSymbols';

const USDC = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'USDC',
    decimals: 6,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].USDC,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].LVUSD,
    lvTokenSymbol: 'LVUSD',
    image: '/tokens/USDC.svg',
    listedAt: {
      [ChainName.MONAD]: new Date('2025-05-06'),
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'USDC',
    poolDisplaySymbol: 'USDC',
    isStable: true,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    color: '#0069BF',
  });

const LVUSD = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'LVUSD',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].LVUSD,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].LVUSD,
    lvTokenSymbol: 'LVUSD',
    image: '/tokens/LVUSD.svg',
    listedAt: {
      [ChainName.MONAD]: new Date('2025-05-06'),
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'LVUSD',
    poolDisplaySymbol: 'LVUSD',
    isStable: true,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    color: '#0069BF',
  });

const MON = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'MON',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].WMON,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].LVMON,
    lvTokenSymbol: 'LVMON',
    image: '/tokens/MON.svg',
    listedAt: {
      [ChainName.MONAD]: notListedDate,
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'MON',
    poolDisplaySymbol: 'MON',
    isStable: false,
    isNative: true,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    color: '#2C3765', // color from icon
  });

const WMON = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'WMON',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].WMON,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].LVMON,
    lvTokenSymbol: 'LVMON',
    image: '/tokens/MON.svg',
    listedAt: {
      [ChainName.MONAD]: notListedDate,
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'WMON',
    poolDisplaySymbol: 'WMON',
    isStable: false,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    color: '#2C3765', // color from icon
  });

const LVMON = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'LVMON',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].LVMON,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].LVMON,
    lvTokenSymbol: 'LVMON',
    image: '/tokens/LVMON.svg',
    listedAt: {
      [ChainName.MONAD]: notListedDate,
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'LVMON',
    poolDisplaySymbol: 'LVMON',
    isStable: false,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    color: '#2C3765', // color from icon
  });

const LV = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'LV',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].LV,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].LV,
    lvTokenSymbol: 'LV',
    image: '/tokens/LV.svg',
    listedAt: {
      [ChainName.MONAD]: new Date('2026-07-03'),
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'LV',
    poolDisplaySymbol: 'LV',
    isStable: false,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    isDirectCollateral: true,
    color: '#7B61FF',
  });

const Rona = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'Rona',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].Rona,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].Rona,
    lvTokenSymbol: 'Rona',
    image: '/tokens/Rona.jpg',
    listedAt: {
      [ChainName.MONAD]: notListedDate,
      [ChainName.MONAD_TESTNET]: new Date('2024-01-01'),
    }[getChainName(chainId)],
    displaySymbol: 'Rona',
    poolDisplaySymbol: 'Rona',
    isStable: false,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    isDirectCollateral: true,
    color: '#7B61FF',
  });

const EMO = (
  chainId: ChainId.MONAD_MAINNET | ChainId.MONAD_TESTNET,
): PoolToken =>
  Object.freeze<PoolToken>({
    symbol: 'EMO',
    decimals: 18,
    address: TOKEN_ADDRESS_BY_CHAIN[chainId].EMO,
    lvTokenAddress: TOKEN_ADDRESS_BY_CHAIN[chainId].EMO,
    lvTokenSymbol: 'EMO',
    image: '/tokens/EMO.avif',
    listedAt: {
      [ChainName.MONAD]: new Date('2026-07-03'),
      [ChainName.MONAD_TESTNET]: notListedDate,
    }[getChainName(chainId)],
    displaySymbol: 'EMO',
    poolDisplaySymbol: 'EMO',
    isStable: false,
    isNative: false,
    isPayToken: true,
    isPoolToken: true,
    isTradable: false,
    isDirectCollateral: true,
    color: '#7B61FF',
  });

export const getPoolTokenList = (
  chainId: ChainId,
  lvToken?: Address,
): PoolToken[] => {
  const mainnetTokens = [
    USDC(chainId),
    MON(chainId),
    // WMON(chainId),
    LV(chainId),
    LVUSD(chainId),
    LVMON(chainId),
    EMO(chainId),
  ];
  const testnetTokens = [
    USDC(chainId),
    MON(chainId),
    // WMON(chainId),
    LVUSD(chainId),
    LVMON(chainId),
    LV(chainId),
    Rona(chainId),
  ];

  const tokens = chainId == ChainId.MONAD_MAINNET ? mainnetTokens : testnetTokens;

  // If lvToken parameter is provided, filter tokens by matching lvTokenAddress
  if (lvToken) {
    return tokens.filter(
      (token) =>
        token.lvTokenAddress.toLowerCase() === lvToken.toLowerCase(),
    );
  }

  return tokens;
}

const tokeMemoryCache: Partial<Record<ChainId, readonly PoolToken[]>> = {};

export const getPoolTokens = (
  chainId: ChainId,
  lvToken?: Address,
): readonly PoolToken[] => {
  // For caching, we only cache when no lvToken filter is applied
  if (!lvToken) {
    const tokenList = tokeMemoryCache[chainId];
    if (tokenList) {
      return tokenList;
    }
    const newTokenList = Object.freeze(getPoolTokenList(chainId));
    tokeMemoryCache[chainId] = newTokenList;

    return newTokenList;
  }

  // When lvToken filter is applied, don't use cache and return filtered results
  return Object.freeze(getPoolTokenList(chainId, lvToken));
};

export const getPayablePoolTokens = (
  chainId: ChainId,
  lvToken?: Address,
): PayablePoolToken[] => {
  return getPoolTokens(chainId, lvToken).filter((t): t is PayablePoolToken => {
    return t.isPayToken === true;
  });
};

export const getPayablePoolTokensWithoutNative = (
  chainId: ChainId,
  lvToken?: Address,
): PayablePoolTokenWithoutNative[] => {
  return getPayablePoolTokens(chainId, lvToken).filter(
    (t): t is PayablePoolTokenWithoutNative => {
      return t.isNative !== true;
    },
  );
};

export const getUnstablePoolTokens = (
  chainId: ChainId,
  lvToken?: Address,
): UnstablePoolToken[] => {
  return getPoolTokens(chainId, lvToken).filter((t): t is UnstablePoolToken => {
    return t.isStable === false;
  });
};

/**
 * @warning this may get native token in the pool
 */
export const getPoolTokenByAddress = (
  address: string,
  chainId: ChainId,
): PoolToken | undefined => {
  return getPoolTokens(chainId).find(
    (v) => v.address.toLowerCase() === address.toLowerCase(),
  );
};

export const getPoolTokenBySymbol = (
  symbol: PoolTokenSymbol,
  chainId: ChainId,
): PoolToken | undefined => {
  return getPoolTokens(chainId).find((v) => v.symbol === symbol);
};

export const getPayablePoolTokenBySymbol = (
  symbol: PoolTokenSymbol,
  chainId: ChainId,
): PayablePoolToken | undefined => {
  return getPayablePoolTokens(chainId).find((v) => v.symbol === symbol);
};
/**
 * @warning this may get native token in the pool
 */
export const getPayablePoolTokenByAddress = (
  address: Address,
  chainId: ChainId,
): PayablePoolToken | undefined => {
  return getPayablePoolTokens(chainId).find(
    (v) => v.address.toLowerCase() === address.toLowerCase(),
  );
};
